/**
 * StepSessionSetup - Étape 4 : Configuration et prévisualisation de la séance HIIT
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, RefreshCw, Clock, Repeat, ArrowLeftRight, Sparkles, Loader2, Video } from 'lucide-react';
import { SESSION_DURATIONS, BODY_ZONES, HIIT_RATIOS } from '../../data/levels';
import { getExercisesForLevel } from '../../data/exercises';
import { ExerciseViewer } from '../ExerciseViewer';
import { ExerciseRecorder, getMocapRecording } from '../ExerciseRecorder';
import goSportAI from '../../services';

export function StepSessionSetup({
  level,
  profile,
  exercises,
  onGenerate,
  onStart,
  onReplaceExercise,
}) {
  const [duration, setDuration] = useState(profile.preferredDuration || 30);
  const [zone, setZone] = useState(profile.preferredZone || 'full');
  const [generated, setGenerated] = useState(exercises?.length > 0);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiTip, setAiTip] = useState(null);
  const [recordingExercise, setRecordingExercise] = useState(null);

  const ratio = HIIT_RATIOS.find(r => r.level === level) || HIIT_RATIOS[0];

  useEffect(() => {
    if (!generated) {
      onGenerate({ duration, zone });
      setGenerated(true);
    }
  }, [generated, onGenerate, duration, zone]);

  const handleRegenerate = () => {
    onGenerate({ duration, zone });
  };

  const handleReplaceOne = (index) => {
    const pool = getExercisesForLevel(level);
    const current = exercises.map(e => e.id);
    const available = pool.filter(e =>
      !current.includes(e.id) &&
      (zone === 'full' || e.zone.includes(zone))
    );
    if (available.length > 0) {
      const replacement = available[Math.floor(Math.random() * available.length)];
      onReplaceExercise(index, replacement);
    }
  };

  const handleAIGenerate = async () => {
    setAiLoading(true);
    setAiTip(null);
    try {
      const cycleTime = ratio.work + ratio.rest;
      const totalCycles = Math.min(ratio.cycles, Math.floor((duration * 60) / cycleTime));

      const result = await goSportAI.generateCustomSession(profile, {
        level,
        zone,
        duration,
        totalCycles,
      });

      if (result?.exercises?.length > 0) {
        // Replace exercises via onReplaceExercise for each
        result.exercises.forEach((ex, i) => {
          onReplaceExercise(i, ex);
        });
        setAiTip(result.motivation || result.focusTip);
      }
    } catch {
      // Silently fall back — exercises stay as they are
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="px-4 py-4 space-y-5"
    >
      <h3 className="text-xl font-bold text-white">Ta séance HIIT</h3>

      {/* Paramètres */}
      <div className="space-y-3">
        {/* Durée */}
        <div>
          <p className="text-xs font-medium text-zinc-400 mb-2">Durée</p>
          <div className="flex gap-2">
            {SESSION_DURATIONS.map(d => (
              <button
                key={d}
                onClick={() => { setDuration(d); setGenerated(false); }}
                className={`flex-1 py-2 rounded-xl text-sm font-bold border transition-all
                  ${d === duration
                    ? 'bg-orange-500/20 border-orange-500/50 text-orange-300'
                    : 'bg-zinc-800/60 border-zinc-700/50 text-zinc-400'
                  }`}
              >
                {d} min
              </button>
            ))}
          </div>
        </div>

        {/* Zone */}
        <div>
          <p className="text-xs font-medium text-zinc-400 mb-2">Zone du corps</p>
          <div className="grid grid-cols-4 gap-2">
            {BODY_ZONES.map(z => (
              <button
                key={z.id}
                onClick={() => { setZone(z.id); setGenerated(false); }}
                className={`py-2 rounded-xl text-center border transition-all
                  ${z.id === zone
                    ? 'bg-orange-500/20 border-orange-500/50 text-orange-300'
                    : 'bg-zinc-800/60 border-zinc-700/50 text-zinc-400'
                  }`}
              >
                <span className="text-lg block">{z.emoji}</span>
                <span className="text-[10px]">{z.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Résumé ratio */}
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-2.5 text-center">
            <Clock className="w-4 h-4 text-red-400 mx-auto mb-1" />
            <p className="text-sm font-bold text-red-300">{ratio.work}s</p>
            <p className="text-[10px] text-red-400/70">Travail</p>
          </div>
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-2.5 text-center">
            <Clock className="w-4 h-4 text-blue-400 mx-auto mb-1" />
            <p className="text-sm font-bold text-blue-300">{ratio.rest}s</p>
            <p className="text-[10px] text-blue-400/70">Repos</p>
          </div>
          <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-2.5 text-center">
            <Repeat className="w-4 h-4 text-orange-400 mx-auto mb-1" />
            <p className="text-sm font-bold text-orange-300">{exercises?.length || ratio.cycles}</p>
            <p className="text-[10px] text-orange-400/70">Cycles</p>
          </div>
        </div>
      </div>

      {/* Liste exercices */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-zinc-300">Exercices</p>
          <div className="flex items-center gap-3">
            <button
              onClick={handleAIGenerate}
              disabled={aiLoading}
              className="flex items-center gap-1 text-xs text-purple-400 hover:text-purple-300
                disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {aiLoading
                ? <><Loader2 className="w-3 h-3 animate-spin" /> IA Coach…</>
                : <><Sparkles className="w-3 h-3" /> IA Coach</>
              }
            </button>
            <button
              onClick={handleRegenerate}
              className="flex items-center gap-1 text-xs text-orange-400 hover:text-orange-300"
            >
              <RefreshCw className="w-3 h-3" /> Régénérer
            </button>
          </div>
        </div>

        {aiTip && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-purple-500/10 border border-purple-500/30 rounded-lg px-3 py-2
              flex items-start gap-2"
          >
            <Sparkles className="w-3.5 h-3.5 text-purple-400 mt-0.5 shrink-0" />
            <p className="text-xs text-purple-300">{aiTip}</p>
          </motion.div>
        )}

        <div className="space-y-1.5 max-h-[300px] overflow-y-auto pr-1">
          {(exercises || []).map((ex, i) => (
            <motion.div
              key={`${ex.id}-${i}`}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.05 }}
              className="flex items-center gap-3 bg-zinc-800/60 border border-zinc-700/50
                rounded-xl px-3 py-2.5 group"
            >
              <div className="w-12 h-12 shrink-0 bg-zinc-700/40 rounded-lg overflow-hidden flex items-center justify-center">
                <ExerciseViewer exerciseId={ex.id} className="w-12 h-12" height={48} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <p className="text-sm font-medium text-zinc-200 truncate">{ex.name}</p>
                  {getMocapRecording(ex.id) && (
                    <span className="text-[8px] bg-green-500/20 text-green-400 px-1 rounded">REC</span>
                  )}
                </div>
                <p className="text-[10px] text-zinc-500">{ex.tip}</p>
              </div>
              <button
                onClick={() => setRecordingExercise(ex)}
                className="opacity-0 group-hover:opacity-100 p-1 text-zinc-500 hover:text-red-400 transition-all"
                title="Enregistrer mouvement"
              >
                <Video className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => handleReplaceOne(i)}
                className="opacity-0 group-hover:opacity-100 p-1 text-zinc-500 hover:text-orange-400 transition-all"
                title="Remplacer"
              >
                <ArrowLeftRight className="w-3.5 h-3.5" />
              </button>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Start button */}
      <motion.button
        whileTap={{ scale: 0.97 }}
        onClick={onStart}
        className="w-full py-4 rounded-2xl bg-gradient-to-r from-orange-500 to-red-500
          text-white font-bold text-lg shadow-lg shadow-orange-500/30
          flex items-center justify-center gap-2"
      >
        <Play className="w-6 h-6" fill="white" />
        C'est parti !
      </motion.button>

      {/* Exercise Recorder overlay */}
      <AnimatePresence>
        {recordingExercise && (
          <ExerciseRecorder
            exerciseId={recordingExercise.id}
            exerciseName={recordingExercise.name}
            onClose={() => setRecordingExercise(null)}
            onRecordingComplete={(id, data) => {
              console.log(`Mocap saved: ${id} (${data.frameCount} frames)`);
            }}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}
